import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id?: string;
  email: string;
  name: string;
  avatar?: string;
  provider?: 'local' | 'google';
  role?: 'student' | 'teacher' | 'admin';
  studentId?: string;
}

/** Lightweight subscription summary surfaced from GET /api/auth/me */
export interface SubscriptionSummary {
  active: boolean;
  status: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  subscription: SubscriptionSummary | null;
  login: (userData: User, token: string) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize from localStorage immediately
  const getInitialUser = () => {
    try {
      const savedUser = localStorage.getItem('cleveroo_user');
      if (savedUser) {
        console.log('🔄 AuthContext: Found user in localStorage');
        return JSON.parse(savedUser);
      }
    } catch (error) {
      console.error('Failed to parse saved user:', error);
    }
    return null;
  };

  const getInitialToken = () => {
    try {
      return localStorage.getItem('cleveroo_token') || null;
    } catch (error) {
      console.error('Failed to get saved token:', error);
    }
    return null;
  };

  const [user, setUser] = useState<User | null>(getInitialUser());
  const [token, setToken] = useState<string | null>(getInitialToken());
  const [isLoading] = useState(false); // Set to false since we load synchronously
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);

  // Log initial state
  useEffect(() => {
    console.log('🔐 AuthContext initialized with:', { user, token, isLoading });
  }, [user, token, isLoading]);

  // Fetch subscription summary whenever the user/token changes
  useEffect(() => {
    const currentToken = token;
    if (!currentToken || !user) {
      setSubscription(null);
      return;
    }

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

    fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${currentToken}` },
    })
      .then((r) => r.json())
      .then((body) => {
        const d = body?.data;
        const sub = d?.subscription;
        if (sub && typeof sub.active === 'boolean') {
          setSubscription({ active: sub.active, status: sub.status ?? '' });
        }
        // Sync server-side profile fields (avatar/name) into the local user so a
        // refresh or another device reflects the latest choice (ROO-222). Guarded
        // so it converges (no setUser once they already match).
        if (d && user && (('avatar' in d && d.avatar !== user.avatar) || (d.name && d.name !== user.name))) {
          const merged = { ...user, avatar: d.avatar ?? user.avatar, name: d.name ?? user.name };
          setUser(merged);
          localStorage.setItem('cleveroo_user', JSON.stringify(merged));
        }
      })
      .catch(() => {
        // Non-fatal — billing data is best-effort
      });
  }, [user, token]);

  const login = (userData: User, userToken: string) => {
    console.log('🔐 AuthContext login called with:', { userData, userToken });

    setUser(userData);
    setToken(userToken);
    setSubscription(null); // will be re-fetched by the effect above

    // Save to localStorage
    localStorage.setItem('cleveroo_token', userToken);
    localStorage.setItem('cleveroo_user', JSON.stringify(userData));

    console.log('💾 AuthContext: User and token stored');
    console.log('✅ AuthContext: Authentication should now be true');
  };

  const logout = () => {
    console.log('🔓 Logging out user:', user?.name);

    setUser(null);
    setToken(null);
    setSubscription(null);

    // Clear localStorage
    localStorage.removeItem('cleveroo_token');
    localStorage.removeItem('cleveroo_user');

    console.log('✅ User logged out successfully');

    // Optionally redirect to login or refresh page
    window.location.reload();
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('cleveroo_user', JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    subscription,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 